import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCopyright,
    faTrademark,
    faLightbulb,
    faSeedling,
    faMicrochip,
    faPaintBrush,
    faCode,
    faMusic,
    faFilm,
    faBook,
    faShieldAlt,
    faSearch,
    faGavel,
    faFileContract,
    faGlobe,
    faUsers,
    faChartLine,
    faAward,
    faCheckCircle,
    faChevronRight,
    faUserTie,
    faCalendarAlt,
    faCertificate,
    faBuilding,
    faClipboardCheck,
    faBalanceScale,
    faHandshake,
    faLock,
    faRobot,
    faIndustry,
    faShoppingBag,
    faVial,
   
} from "@fortawesome/free-solid-svg-icons";
import { Cta } from "../components/Cta";
import { useNavigate } from "react-router-dom";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const ipServices = [
    {
        icon: faTrademark,
        title: "Trademark Protection",
        description: "Comprehensive trademark registration, search, and enforcement services worldwide.",
        color: "gold",
        subservices: [
            "Trademark Search & Clearance",
            "Registration & Prosecution",
            "Opposition & Cancellation",
            "Trademark Monitoring"
        ],
        stat: "5000+"
    },
    {
        icon: faCopyright,
        title: "Copyright Registration",
        description: "Protection for creative works including software, literature, music, and artworks.",
        color: "primary-blue",
        subservices: [
            "Copyright Registration",
            "Software & Tech Protection",
            "Entertainment & Media",
            "Design & Architectural Works"
        ],
        stat: "3000+"
    },
    // {
    //     icon: faLightbulb,
    //     title: "Patent Services",
    //     description: "Patent drafting, filing, and prosecution for inventions across all technology sectors.",
    //     color: "gold",
    //     subservices: [
    //         "Patent Search & Analysis",
    //         "Drafting & Filing",
    //         "Prosecution & Grant",
    //         "Patent Licensing"
    //     ],
    //     stat: "1500+"
    // },
    {
        icon: faSeedling,
        title: "Design Registration",
        description: "Protection for industrial designs, product designs, and aesthetic creations.",
        color: "primary-blue",
        subservices: [
            "Design Search",
            "Registration & Protection",
            "Design Portfolio Management",
            "Infringement Actions"
        ],
        stat: "2000+"
    }
];

const ipTypes = [
    {
        type: "Trademarks",
        icon: faTrademark,
        description: "Protection for brand names, logos, slogans, and trade dress",
        examples: ["Brand Names", "Logos", "Slogans", "Trade Dress"],
        duration: "10 years renewable",
        color: "gold"
    },
    {
        type: "Patents",
        icon: faLightbulb,
        description: "Exclusive rights for inventions and technological innovations",
        examples: ["Inventions", "Processes", "Machines", "Compositions"],
        duration: "20 years",
        color: "primary-blue"
    },
    {
        type: "Copyrights",
        icon: faCopyright,
        description: "Protection for original creative and artistic works",
        examples: ["Books", "Music", "Software", "Films"],
        duration: "Life + 60 years",
        color: "gold"
    },
    {
        type: "Designs",
        icon: faPaintBrush,
        description: "Protection for visual appearance and aesthetic designs",
        examples: ["Product Shapes", "Patterns", "Ornamentation", "Packaging"],
        duration: "10+5+5 years",
        color: "primary-blue"
    },
    // {
    //     type: "Trade Secrets",
    //     icon: faLock,
    //     description: "Confidential business information with commercial value",
    //     examples: ["Formulas", "Processes", "Methods", "Customer Lists"],
    //     duration: "Indefinite",
    //     color: "gold"
    // }
];

const industries = [
    {
        industry: "Technology & Software",
        icon: faCode,
        description: "IP protection for software, apps, and digital innovations",
        services: ["Software Copyright", "Patent Protection", "Open Source", "Licensing"],
        color: "primary-blue"
    },
    {
        industry: "Pharmaceutical & Biotech",
        icon: faVial,
        description: "Patent protection for drugs, formulations, and medical devices",
        services: ["Drug Patents", "Process Patents", "Clinical Data", "Regulatory"],
        color: "gold"
    },
    {
        industry: "Entertainment & Media",
        icon: faFilm,
        description: "Copyright protection for creative content and media productions",
        services: ["Film Rights", "Music Copyright", "Publishing", "Distribution"],
        color: "primary-blue"
    },
    {
        industry: "Fashion & Retail",
        icon: faShoppingBag,
        description: "Trademark and design protection for fashion brands",
        services: ["Brand Protection", "Design Rights", "Counterfeit", "E-commerce"],
        color: "gold"
    },
    {
        industry: "Manufacturing",
        icon: faIndustry,
        description: "Industrial designs and process patent protection",
        services: ["Industrial Designs", "Process Patents", "Trade Secrets", "Quality Marks"],
        color: "primary-blue"
    },
    {
        industry: "Artificial Intelligence",
        icon: faRobot,
        description: "IP strategies for AI algorithms and machine learning models",
        services: ["Algorithm Patents", "Data Rights", "AI Copyright", "Ethical IP"],
        color: "gold"
    }
];

const enforcementServices = [
    {
        service: "IP Litigation",
        icon: faGavel,
        description: "Representation in IP disputes and infringement cases",
        areas: ["Infringement Suits", "Counterfeiting", "Passing Off", "Cybersquatting"],
        successRate: "90%",
        color: "gold"
    },
    {
        service: "IP Audits",
        icon: faSearch,
        description: "Comprehensive IP portfolio review and strategy development",
        areas: ["Portfolio Audit", "Valuation", "Gap Analysis", "Strategy Planning"],
        stat: "200+",
        color: "primary-blue"
    },
    {
        service: "Licensing & Agreements",
        icon: faFileContract,
        description: "Drafting and negotiation of IP licensing agreements",
        areas: ["License Agreements", "Franchising", "Technology Transfer", "Joint Ventures"],
        stat: "500+",
        color: "gold"
    },
    {
        service: "International IP",
        icon: faGlobe,
        description: "Global IP protection and international registration",
        areas: ["PCT Applications", "Madrid Protocol", "EUIPO", "WIPO"],
        countries: "50+",
        color: "primary-blue"
    }
];

const caseStudies = [
    {
        title: "Brand Protection Victory",
        client: "Global Fashion Brand",
        issue: "Widespread counterfeit operations across 5 countries",
        solution: "Multi-jurisdictional enforcement strategy",
        result: "₹25 Crore damages awarded",
        seizures: "100,000+ counterfeit items",
        icon: faTrademark
    },
    {
        title: "Patent Infringement Case",
        client: "Pharmaceutical Company",
        issue: "Generic manufacturer infringing patented drug formula",
        solution: "Expert evidence and strategic litigation",
        result: "Permanent injunction granted",
        duration: "18 months",
        icon: faLightbulb
    },
    {
        title: "Software Copyright Dispute",
        client: "Tech Startup",
        issue: "Source code theft by former employee",
        solution: "Urgent injunction and evidence preservation",
        result: "Complete code recovery + damages",
        savings: "₹8 Crore",
        icon: faCopyright
    },
    {
        title: "Design Infringement Action",
        client: "Consumer Electronics",
        issue: "Copycat product design in market",
        solution: "Design registration enforcement",
        result: "Product recall + destruction",
        marketProtection: "3-year exclusivity",
        icon: faPaintBrush
    }
];

const ipProcess = [
    {
        step: 1,
        title: "IP Audit & Strategy",
        description: "Comprehensive analysis of existing and potential IP assets",
        icon: faSearch,
        duration: "2-4 weeks",
        color: "gold"
    },
    {
        step: 2,
        title: "Search & Clearance",
        description: "Thorough search to ensure availability and avoid conflicts",
        icon: faClipboardCheck,
        duration: "3-6 weeks",
        color: "primary-blue"
    },
    {
        step: 3,
        title: "Registration & Filing",
        description: "Drafting and filing applications with IP offices",
        icon: faFileContract,
        duration: "Varies by IP type",
        color: "gold"
    },
    {
        step: 4,
        title: "Prosecution",
        description: "Responding to objections and securing registration",
        icon: faBalanceScale,
        duration: "6-36 months",
        color: "primary-blue"
    },
    {
        step: 5,
        title: "Maintenance & Enforcement",
        description: "Ongoing protection and infringement monitoring",
        icon: faShieldAlt,
        duration: "Ongoing",
        color: "gold"
    }
];

const whyChooseUs = [
    {
        reason: "Technology-Savvy IP Experts",
        description: "Specialized in software, AI, and digital IP protection",
        icon: faMicrochip,
        color: "gold"
    },
    // {
    //     reason: "Global IP Network",
    //     description: "Partnerships with IP firms in 50+ countries",
    //     icon: faGlobe,
    //     color: "primary-blue"
    // },
    // {
    //     reason: "Industry Specialization",
    //     description: "Deep expertise in pharma, tech, entertainment sectors",
    //     icon: faIndustry,
    //     color: "gold"
    // },
    {
        reason: "Proactive Protection",
        description: "Preventive strategies and portfolio management",
        icon: faShieldAlt,
        color: "primary-blue"
    }
];

export const IntellectualPropertyLaw = () => {
    const [activeService, setActiveService] = useState(0);
    const [activeTab, setActiveTab] = useState("registration");
    const navigate=useNavigate();

    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px",
            position: "relative"
        }}>
            {/* Background decorative elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
                <div className="position-absolute top-0 end-0 w-25 h-25"
                    style={{
                        background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(30%, -30%)"
                    }}
                ></div>
                <div className="position-absolute bottom-0 start-0 w-30 h-30"
                    style={{
                        background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(-30%, 30%)"
                    }}
                ></div>
                {/* IP Pattern */}
                <div className="position-absolute top-50 start-50 translate-middle w-100 h-100"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23b8860b' fill-opacity='0.03' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                        opacity: 0.1
                    }}
                ></div>
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div variants={fadeInUp}>
                        <span className="d-inline-block px-4 py-2 rounded-pill mb-3"
                            style={{
                                color: "var(--gold)",
                                fontWeight: "600",
                                letterSpacing: "2px",
                                fontSize: "0.9rem",
                                textTransform: "uppercase",
                                border: "1px solid var(--gold-light)",
                                background: "rgba(184, 134, 11, 0.05)",
                                backdropFilter: "blur(4px)"
                            }}
                        >
                            Intellectual Property Specialists
                        </span>



                        <motion.h4
                            className="fw-bold display-6 display-md-5 mt-2"
                            variants={fadeInUp}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontWeight: 600,
                                color: 'var(--primary-dark)',
                                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            Intellectual Property Specialists <br/> (IP Protection & Enforcement)
                        </motion.h4>
                    </motion.div>

                    <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        Comprehensive intellectual property services covering trademarks, copyrights,
                        and designs. 
                    </motion.p>
                </motion.div>

                {/* IP Services Tabs */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <div className="d-flex flex-wrap justify-content-center gap-3 mb-4">
                            {["registration", "enforcement", "licensing", "audit"].map((tab) => (
                                <motion.button
                                    key={tab}
                                    type="button"
                                    className={`btn px-4 py-3 ${activeTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                    style={{
                                        backgroundColor: activeTab === tab ? 'var(--gold)' : 'transparent',
                                        color: activeTab === tab ? 'var(--white)' : 'var(--gold)',
                                        border: `2px solid var(--gold)`,
                                        fontWeight: "600",
                                        textTransform: "capitalize",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {tab === 'registration' && <FontAwesomeIcon icon={faFileContract} className="me-2" />}
                                    {tab === 'enforcement' && <FontAwesomeIcon icon={faGavel} className="me-2" />}
                                    {tab === 'licensing' && <FontAwesomeIcon icon={faHandshake} className="me-2" />}
                                    {tab === 'audit' && <FontAwesomeIcon icon={faSearch} className="me-2" />}
                                    {tab}
                                </motion.button>
                            ))}
                        </div>

                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faCertificate} className="me-3" style={{ color: "var(--gold)" }} />
                            IP Protection Services
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            End-to-end intellectual property services from registration to enforcement
                        </p>
                    </div>

                    {/* Services Grid */}
                    <div className="row g-4 d-flex justify-content-center">
                        {ipServices.map((service, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                                onMouseEnter={() => setActiveService(index)}
                            >
                                <motion.div
                                    className="h-100 p-4 rounded-4 position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `1px solid var(--${service.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -8,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${service.color})`
                                    }}
                                >
                                    <div className="position-relative z-1 h-100 d-flex flex-column">
                                        {/* Icon & Stat */}
                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                            <motion.div
                                                className="p-3 rounded-3"
                                                style={{
                                                    backgroundColor: `rgba(var(--${service.color}-rgb), 0.1)`,
                                                    color: `var(--${service.color})`
                                                }}
                                                whileHover={{ rotate: 360 }}
                                                transition={{ duration: 0.6, ease: "easeInOut" }}
                                            >
                                                <FontAwesomeIcon icon={service.icon} style={{ fontSize: "1.5rem" }} />
                                            </motion.div>

                                            {/* <motion.div
                                                className="display-6 fw-bold"
                                                style={{
                                                    color: `var(--${service.color})`,
                                                    fontFamily: "'Playfair Display', serif"
                                                }}
                                                animate={{
                                                    scale: activeService === index ? 1.1 : 1
                                                }}
                                                transition={{ type: "spring", stiffness: 300 }}
                                            >
                                                {service.stat}
                                            </motion.div> */}
                                        </div>

                                        {/* Title & Description */}
                                        <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                            {service.title}
                                        </h5>

                                        <p className="mb-4 flex-grow-1 small"
                                            style={{
                                                color: "var(--text-primary)",
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {service.description}
                                        </p>

                                        {/* Subservices */}
                                        <div className="mt-auto pt-3 border-top"
                                            style={{ borderColor: `var(--${service.color}-light)` }}
                                        >
                                            <div className="vstack gap-2">
                                                {service.subservices.map((subservice, idx) => (
                                                    <motion.div
                                                        key={idx}
                                                        className="d-flex align-items-center gap-2"
                                                        initial={{ opacity: 0, x: -10 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: idx * 0.1 }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faChevronRight}
                                                            style={{
                                                                color: `var(--${service.color})`,
                                                                fontSize: "0.7rem"
                                                            }}
                                                        />
                                                        <span className="small" style={{ color: "var(--text-primary)" }}>
                                                            {subservice}
                                                        </span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Types of IP */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faShieldAlt} className="me-3" style={{ color: "var(--gold)" }} />
                            Types of Intellectual Property
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Comprehensive protection for all forms of intellectual property assets
                        </p>
                    </div>

                    <div className="row g-4">
                        {ipTypes.map((ipType, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${ipType.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${ipType.color})`
                                    }}
                                >
                                    <div className="text-center mb-3">
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${ipType.color}-rgb), 0.1)`,
                                                color: `var(--${ipType.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={ipType.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </div>

                                    <h5 className="fw-bold text-center mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {ipType.type}
                                    </h5>

                                    <p className="small text-center mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6
                                    }}>
                                        {ipType.description}
                                    </p>

                                    <div className="mb-4">
                                        <div className="small fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                            Examples:
                                        </div>
                                        <div className="vstack gap-1">
                                            {ipType.examples.map((example, idx) => (
                                                <div key={idx} className="d-flex align-items-center gap-2">
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        style={{
                                                            color: `var(--${ipType.color})`,
                                                            fontSize: "0.7rem"
                                                        }}
                                                    />
                                                    <span className="small" style={{ color: "var(--text-primary)" }}>
                                                        {example}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="pt-3 border-top text-center"
                                        style={{ borderColor: `var(--${ipType.color}-light)` }}
                                    >
                                        <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                            Protection Duration
                                        </div>
                                        <div className="fw-bold" style={{ color: `var(--${ipType.color})` }}>
                                            {ipType.duration}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Industry Specialization */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faIndustry} className="me-3" style={{ color: "var(--gold)" }} />
                            Industry-Specific IP Solutions
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Tailored intellectual property strategies for different industries
                        </p>
                    </div>

                    <div className="row g-4">
                        {industries.map((industry, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-4"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${industry.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${industry.color})`
                                    }}
                                >
                                    <div className="d-flex align-items-start gap-3 mb-3">
                                        <div className="p-2 rounded-3 flex-shrink-0"
                                            style={{
                                                backgroundColor: `rgba(var(--${industry.color}-rgb), 0.1)`,
                                                color: `var(--${industry.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={industry.icon} style={{ fontSize: "1.2rem" }} />
                                        </div>
                                        <div>
                                            <h6 className="fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                {industry.industry}
                                            </h6>
                                            <p className="small mb-0" style={{ color: "var(--text-primary)" }}>
                                                {industry.description}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-top"
                                        style={{ borderColor: `var(--${industry.color}-light)` }}
                                    >
                                        <div className="small fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                            Key Services:
                                        </div>
                                        <div className="row g-2">
                                            {industry.services.map((service, idx) => (
                                                <div key={idx} className="col-6">
                                                    <div className="d-flex align-items-center gap-1">
                                                        <FontAwesomeIcon
                                                            icon={faCheckCircle}
                                                            style={{
                                                                color: `var(--${industry.color})`,
                                                                fontSize: "0.7rem"
                                                            }}
                                                        />
                                                        <span className="small" style={{ color: "var(--text-primary)" }}>
                                                            {service}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* IP Enforcement Services */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faGavel} className="me-3" style={{ color: "var(--gold)" }} />
                            IP Enforcement & Protection
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Aggressive enforcement and comprehensive protection strategies
                        </p>
                    </div>

                    <div className="row g-4">
                        {enforcementServices.map((service, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${service.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${service.color})`
                                    }}
                                >
                                    <div className="text-center mb-3">
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${service.color}-rgb), 0.1)`,
                                                color: `var(--${service.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={service.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </div>

                                    <h5 className="fw-bold text-center mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {service.service}
                                    </h5>

                                    <p className="small text-center mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6
                                    }}>
                                        {service.description}
                                    </p>

                                    <div className="mb-4">
                                        <div className="small fw-bold mb-2 text-center" style={{ color: "var(--primary-dark)" }}>
                                            Key Areas:
                                        </div>
                                        <div className="vstack gap-1">
                                            {service.areas.map((area, idx) => (
                                                <div key={idx} className="d-flex align-items-center gap-2">
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        style={{
                                                            color: `var(--${service.color})`,
                                                            fontSize: "0.7rem"
                                                        }}
                                                    />
                                                    <span className="small" style={{ color: "var(--text-primary)" }}>
                                                        {area}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="pt-3 border-top text-center"
                                        style={{ borderColor: `var(--${service.color}-light)` }}
                                    >
                                        {service.successRate ? (
                                            <>
                                                <div className="display-6 fw-bold mb-1" style={{ color: `var(--${service.color})` }}>
                                                    {service.successRate}
                                                </div>
                                                <div className="small" style={{ color: "var(--text-primary)" }}>
                                                    Success Rate
                                                </div>
                                            </>
                                        ) : service.stat ? (
                                            <>
                                                <div className="display-6 fw-bold mb-1" style={{ color: `var(--${service.color})` }}>
                                                    {service.stat}
                                                </div>
                                                <div className="small" style={{ color: "var(--text-primary)" }}>
                                                    {service.service === "International IP" ? "Countries Covered" : "Projects Completed"}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="display-6 fw-bold mb-1" style={{ color: `var(--${service.color})` }}>
                                                    {service.countries}
                                                </div>
                                                <div className="small" style={{ color: "var(--text-primary)" }}>
                                                    Countries
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* IP Process */}
                 {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faClipboardCheck} className="me-3" style={{ color: "var(--gold)" }} />
                            Our IP Protection Process
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            A systematic approach to securing and protecting your intellectual property
                        </p>
                    </div>

                    <div className="position-relative">
                      
                        <div className="position-absolute top-50 start-0 w-100 h-2 d-none d-lg-block"
                            style={{
                                backgroundColor: "var(--gold-light)",
                                opacity: 0.3,
                                zIndex: 0
                            }}
                        ></div>

                        <div className="row g-4 position-relative" style={{ zIndex: 1 }}>
                            {ipProcess.map((step, index) => (
                                <motion.div
                                    key={index}
                                    className="col-md-6 col-lg"
                                    variants={scaleIn}
                                >
                                    <motion.div
                                        className="p-4 rounded-4 h-100 text-center"
                                        style={{
                                            backgroundColor: "var(--white)",
                                            border: `2px solid var(--${step.color})`,
                                            boxShadow: "var(--shadow-soft)",
                                            transition: "var(--transition)"
                                        }}
                                        whileHover={{
                                            y: -5,
                                            boxShadow: "var(--shadow-medium)"
                                        }}
                                    >
                                        <div className="position-relative mb-3">
                                            <div className="display-4 fw-bold mb-2" style={{
                                                color: `var(--${step.color})`,
                                                fontFamily: "'Playfair Display', serif"
                                            }}>
                                                0{step.step}
                                            </div>
                                            <div className="position-absolute top-0 start-50 translate-middle"
                                                style={{
                                                    width: "60px",
                                                    height: "60px",
                                                    backgroundColor: `rgba(var(--${step.color}-rgb), 0.1)`,
                                                    borderRadius: "50%",
                                                    zIndex: -1
                                                }}
                                            ></div>
                                        </div>

                                        <div className="p-3 rounded-circle d-inline-block mb-3"
                                            style={{
                                                backgroundColor: `rgba(var(--${step.color}-rgb), 0.1)`,
                                                color: `var(--${step.color})`,
                                                border: `2px solid var(--${step.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={step.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>

                                        <h6 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                            {step.title}
                                        </h6>

                                        <p className="small mb-3" style={{
                                            color: "var(--text-primary)",
                                            lineHeight: 1.5
                                        }}>
                                            {step.description}
                                        </p>

                                        <div className="mt-3 pt-3 border-top"
                                            style={{ borderColor: `var(--${step.color}-light)` }}
                                        >
                                            <div className="small fw-bold" style={{ color: `var(--${step.color})` }}>
                                                {step.duration}
                                            </div>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>  */}

                {/* Case Studies */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faAward} className="me-3" style={{ color: "var(--gold)" }} />
                            IP Protection Success Stories
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Significant victories and successful IP enforcement cases
                        </p>
                    </div>

                    <div className="row g-4">
                        {caseStudies.map((caseStudy, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: "var(--gold)"
                                    }}
                                >
                                    <div className="mb-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <div className="p-2 rounded-3 flex-shrink-0"
                                                style={{
                                                    backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                    color: "var(--gold)"
                                                }}
                                            >
                                                <FontAwesomeIcon icon={caseStudy.icon} />
                                            </div>
                                            <h6 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                                                {caseStudy.title}
                                            </h6>
                                        </div>
                                        <div className="small mb-3" style={{ color: "var(--primary-blue)" }}>
                                            Client: {caseStudy.client}
                                        </div>
                                    </div>

                                    <div className="vstack gap-3 mb-4">
                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Challenge
                                            </div>
                                            <div className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.issue}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Our Strategy
                                            </div>
                                            <div className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.solution}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Result
                                            </div>
                                            <div className="small fw-bold" style={{ color: "var(--green-accent)" }}>
                                                {caseStudy.result}
                                            </div>
                                        </div>

                                        {caseStudy.seizures && (
                                            <div>
                                                <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                    Counterfeit Seizures
                                                </div>
                                                <div className="small fw-bold" style={{ color: "var(--gold)" }}>
                                                    {caseStudy.seizures}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Why Choose RNA */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="p-4 p-md-5 rounded-4"
                        style={{
                            backgroundColor: "var(--primary-dark)",
                            backgroundImage: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-blue) 100%)",
                            border: "1px solid var(--gold-light)"
                        }}
                    >
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <motion.div variants={fadeInUp}>
                                    <h3 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        <FontAwesomeIcon icon={faUserTie} className="me-3" style={{ color: "var(--gold)" }} />
                                        Why Choose RNA for IP Protection?
                                    </h3>

                                    <div className="row g-4 mb-4">
                                        {whyChooseUs.map((reason, index) => (
                                            <div key={index} className="col-md-6">
                                                <div className="d-flex align-items-start gap-3 mb-3">
                                                    <div className="p-2 rounded-3 flex-shrink-0"
                                                        style={{
                                                            backgroundColor: `rgba(var(--${reason.color}-rgb), 0.2)`,
                                                            color: `var(--${reason.color})`
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={reason.icon} />
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                            {reason.reason}
                                                        </div>
                                                        <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                            {reason.description}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* <div className="row g-4">
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    5000+
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Trademarks Registered
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    90%
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Enforcement Success Rate
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    50+
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Countries Covered
                                                </div>
                                            </div>
                                        </div>
                                    </div> */}
                                </motion.div>
                            </div>

                            <div className="col-lg-4 text-center mt-4 mt-lg-0">
                                <motion.div
                                    className="p-4 rounded-4"
                                    style={{
                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                        backdropFilter: "blur(10px)",
                                        border: "1px solid rgba(255, 255, 255, 0.2)"
                                    }}
                                    variants={itemVariants}
                                >
                                    <FontAwesomeIcon
                                        icon={faLightbulb}
                                        className="mb-3"
                                        style={{
                                            fontSize: "3rem",
                                            color: "var(--gold)"
                                        }}
                                    />
                                    <h5 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        Free IP  Consultation
                                    </h5>
                                    <p className="small mb-4" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                        Get a comprehensive review of your IP portfolio
                                    </p>
                                    <motion.button
                                      onClick={() => navigate("/contact")}
                                        className="btn w-100 py-3"
                                        style={{
                                            backgroundColor: "var(--gold)",
                                            color: "var(--primary-dark)",
                                            fontWeight: "600",
                                            border: "none"
                                        }}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                                        Schedule Now
                                    </motion.button>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

              
                <Cta Title="Protect Your Innovations Today" description="Contact our intellectual property specialists for comprehensive protection strategies"/>
            </div>
        </div>
    );
};